import { components } from "@/api-schema";
import {
  Action,
  PathsV1BranchSearchPostParametersQueryExtend,
  PathsV1BranchSearchPostParametersQueryOrderByDirection,
  PathsV1BranchSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { Can, useAbility } from "@/casl-provider";
import { BranchSearchBody, useBranchSearchQuery } from "@/common/api/branch/use-branch-search.query";
import { createRowNavigatePath } from "@/components/data-table/_helpers/create-row-navigate-path";
import { BranchTable } from "@/components/data-table/predefined/branch-table";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { CompanyFilter } from "@/components/filter/predefined/company-filter";
import { CompanyGroupFilter } from "@/components/filter/predefined/company-group-filter";
import { DeletedStatus, DeletedStatusFilter } from "@/components/filter/predefined/deleted-status-filter";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { LocationMap } from "@/components/location-map/location-map";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertCircle, CheckCircle2, MapIcon, MapPin, Navigation, Target, X, Zap } from "lucide-react";
import { useCallback, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type Branch = components["schemas"]["Branch"];
type BranchWithDistance = Branch & { distance?: number };

const DEFAULT_PAGE_SIZE = 100;
const PROXIMITY_SORTING = [
  {
    field: PathsV1BranchSearchPostParametersQueryOrderByField.Distance,
    direction: PathsV1BranchSearchPostParametersQueryOrderByDirection.Asc,
  },
];

export function BranchView() {
  const { t } = useTranslation(["masterdata", "common"]);
  const ability = useAbility();
  const canManage = ability.can(Action.Manage, "Branch");
  const defaultRowActions = canManage
    ? undefined
    : {
        showEdit: false,
        showDelete: false,
        showRestore: false,
      };
  const pageTitle = t("masterdata:AllBranches");
  const pageSubtitle = t("masterdata:FindBranchesNearYouBasedOnCurrentLocation");
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    companyGroup: [],
    companies: [],
    brands: [],
    deletedAt: [DeletedStatus.ACTIVE],
  });

  const filters = useMemo(
    () => [new CompanyGroupFilter(), new CompanyFilter(), new BrandFilter(), new DeletedStatusFilter()],
    [],
  );

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterId]: values,
    }));
  }, []);

  const [userLocation, setUserLocation] = useState<{
    latitude: number;
    longitude: number;
    accuracy?: number;
    isManuallyAdjusted?: boolean;
  } | null>(null);
  const [radiusKm, setRadiusKm] = useState<number>(50);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [showLocationAdjustment, setShowLocationAdjustment] = useState(false);
  const radiusSliderId = useId();

  const getLocationErrorMessage = useCallback(
    (error: GeolocationPositionError): string => {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          return t("masterdata:GeolocationAccessHasBeenDenied");
        case error.POSITION_UNAVAILABLE:
          return t("masterdata:LocationUnavailable");
        case error.TIMEOUT:
          return t("masterdata:LocationTimeout");
        default:
          return t("masterdata:ErrorGettingGeolocationPosition");
      }
    },
    [t],
  );

  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError(t("masterdata:GeolocationIsNotSupportedByThisBrowser"));
      return;
    }

    setIsGettingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setIsGettingLocation(false);
      },
      (error) => {
        if (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setUserLocation({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
              });
              setIsGettingLocation(false);
            },
            (fallbackError) => {
              setLocationError(getLocationErrorMessage(fallbackError));
              setIsGettingLocation(false);
            },
            {
              enableHighAccuracy: false,
              timeout: 15000,
              maximumAge: 600000,
            },
          );
        } else {
          setLocationError(getLocationErrorMessage(error));
          setIsGettingLocation(false);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 60000,
      },
    );
  }, [getLocationErrorMessage, t]);

  const clearLocation = useCallback(() => {
    setUserLocation(null);
    setLocationError(null);
    setShowLocationAdjustment(false);
  }, []);

  const handleLocationAdjustment = useCallback(
    (lat: number, lng: number) => {
      if (userLocation) {
        setUserLocation({
          ...userLocation,
          latitude: lat,
          longitude: lng,
          isManuallyAdjusted: true,
        });
        setShowLocationAdjustment(false);
      }
    },
    [userLocation],
  );

  const getAccuracyLevel = (accuracy?: number) => {
    if (!accuracy)
      return {
        level: t("fit:AccuracyUnknown"),
        color: "muted",
        icon: Target,
        description: t("masterdata:AccuracyUnknownDescription"),
      };
    if (accuracy <= 5)
      return {
        level: t("masterdata:AccuracyVeryHigh"),
        color: ButtonColorVariant.Primary,
        icon: Zap,
        description: t("masterdata:GPSAccurate5m"),
      };
    if (accuracy <= 15)
      return {
        level: t("masterdata:AccuracyHigh"),
        color: ButtonColorVariant.Primary,
        icon: CheckCircle2,
        description: t("masterdata:AccuracyHighDescription"),
      };
    if (accuracy <= 50)
      return {
        level: t("masterdata:AccuracyMedium"),
        color: ButtonColorVariant.Secondary,
        icon: Target,
        description: t("masterdata:AccuracyMediumDescription"),
      };
    if (accuracy <= 100)
      return {
        level: t("masterdata:AccuracyLow"),
        color: ButtonColorVariant.Secondary,
        icon: AlertCircle,
        description: t("masterdata:AccuracyLowDescription"),
      };
    return {
      level: t("masterdata:AccuracyVeryLow"),
      color: ButtonColorVariant.Error,
      icon: X,
      description: t("masterdata:AccuracyVeryLowDescription"),
    };
  };

  const getAccuracyColorClassName = (color: string): string => {
    if (color === ButtonColorVariant.Primary) return "text-primary";
    if (color === ButtonColorVariant.Secondary) return "text-secondary";
    if (color === ButtonColorVariant.Error) return "text-error";
    return "text-muted-foreground";
  };

  const accuracyLevel = userLocation ? getAccuracyLevel(userLocation.accuracy) : null;
  const AccuracyLevelIcon = accuracyLevel?.icon;
  const accuracyLevelColorClassName = accuracyLevel
    ? getAccuracyColorClassName(accuracyLevel.color)
    : "text-muted-foreground";

  const shouldUseBranchData = !!userLocation;

  const branchTableName = shouldUseBranchData ? "branch-table-proximity" : "branch-table";

  const searchBody = useMemo(() => {
    const body: BranchSearchBody = shouldUseBranchData
      ? {
          centerLatitude: userLocation.latitude,
          centerLongitude: userLocation.longitude,
          radiusKm: radiusKm,
        }
      : {};

    if (filterValues.companyGroup.length > 0) {
      body.companyGroup = filterValues.companyGroup;
    }
    if (filterValues.companies.length > 0) {
      body.company = filterValues.companies;
    }
    if (filterValues.brands.length > 0) {
      body.brands = filterValues.brands;
    }
    if (filterValues.deletedAt?.length > 0) {
      body.deletedAt = filterValues.deletedAt;
    } else {
      body.deletedAt = [DeletedStatus.ACTIVE, DeletedStatus.DELETED];
    }

    return body;
  }, [shouldUseBranchData, userLocation, radiusKm, filterValues]);

  const branchQuery = useBranchSearchQuery({
    options: {
      body: searchBody,
      parameters: {
        extend: [
          PathsV1BranchSearchPostParametersQueryExtend.Address,
          PathsV1BranchSearchPostParametersQueryExtend.Company,
          PathsV1BranchSearchPostParametersQueryExtend.Brands,
        ],
      },
    },
  });

  const branchesForMap = useMemo(() => {
    if (!shouldUseBranchData || !branchQuery?.data?.data || !Array.isArray(branchQuery.data.data)) return [];

    return branchQuery.data.data
      .map((branch: BranchWithDistance) => {
        const latitude =
          typeof branch?.address?.latitude === "number" && !Number.isNaN(branch.address.latitude)
            ? branch.address.latitude
            : null;
        const longitude =
          typeof branch?.address?.longitude === "number" && !Number.isNaN(branch.address.longitude)
            ? branch.address.longitude
            : null;
        const name =
          `${branch?.address?.street || ""} ${branch?.address?.houseNumber || ""}, ${branch?.address?.city || ""}`.trim();
        const distance =
          typeof branch?.distance === "number" && !Number.isNaN(branch.distance) ? branch.distance : undefined;

        return {
          latitude,
          longitude,
          name,
          distance,
        };
      })
      .filter((branch) => branch.latitude !== null && branch.longitude !== null)
      .map((branch) => ({
        latitude: branch.latitude as number,
        longitude: branch.longitude as number,
        name: branch.name,
        distance: branch.distance,
      }));
  }, [shouldUseBranchData, branchQuery?.data?.data]);

  return (
    <Can I="read" a="Branch">
      <CenteredLayout
        variant={CenteredLayoutVariant.Table}
        title={pageTitle}
        subTitle={pageSubtitle}
        className="min-h-0 flex-1"
      >
        <div className="flex min-h-0 flex-1 flex-col space-y-6">
          <Card className="shrink-0 gap-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2">
                    <Navigation className="text-primary h-5 w-5" />
                    {t("masterdata:ProximitySearch")}
                  </CardTitle>
                  <CardDescription>{t("masterdata:FindBranchesNearYouBasedOnCurrentLocation")}</CardDescription>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {!userLocation && (
                    <Button
                      onClick={getUserLocation}
                      disabled={isGettingLocation}
                      size={ButtonSize.Sm}
                      className="hidden sm:flex"
                    >
                      {isGettingLocation ? (
                        <>
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2" />
                          {t("masterdata:GPSIsBeingDetermined")}
                        </>
                      ) : (
                        <>
                          <MapPin className="mr-2 h-4 w-4" />
                          {t("masterdata:UseMyLocation")}
                        </>
                      )}
                    </Button>
                  )}
                  {userLocation && (
                    <Button
                      variant={ButtonVariant.Outlined}
                      colorVariant={ButtonColorVariant.Secondary}
                      size={ButtonSize.Sm}
                      onClick={clearLocation}
                    >
                      <X className="mr-2 h-4 w-4" />
                      {t("common:Reset")}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {userLocation ? (
                <div className="mt-6 space-y-2">
                  <div className="text-muted-foreground bg-muted/50 flex items-center gap-2 rounded-md px-3 py-2 text-sm">
                    <CheckCircle2 className="text-primary h-4 w-4" />
                    <span>
                      {t("common:Location")}: {userLocation.latitude.toFixed(6)}, {userLocation.longitude.toFixed(6)}
                    </span>
                    {userLocation.accuracy && accuracyLevel && AccuracyLevelIcon && (
                      <div className="ml-auto flex items-center gap-1">
                        <AccuracyLevelIcon className={`h-3 w-3 ${accuracyLevelColorClassName}`} />
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-xs">±{Math.round(userLocation.accuracy)}m</span>
                            </TooltipTrigger>
                            <TooltipContent>{accuracyLevel.description}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}
                  </div>

                  {userLocation.accuracy && userLocation.accuracy > 50 && (
                    <Alert className="py-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        <div className="flex items-center justify-between">
                          <span>
                            <strong>
                              {t("masterdata:LowAccuracy", { accuracy: Math.round(userLocation.accuracy) })}:
                            </strong>{" "}
                            {t("masterdata:TryOutsideOrNearWindowForBetterGPS")}
                          </span>
                          <Button
                            size={ButtonSize.Sm}
                            variant={ButtonVariant.Outlined}
                            colorVariant={ButtonColorVariant.Secondary}
                            onClick={() => setShowLocationAdjustment(true)}
                            className="ml-2 h-6 px-2 text-xs"
                          >
                            {t("masterdata:Adjust")}
                          </Button>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  {userLocation.isManuallyAdjusted && (
                    <div className="text-primary flex items-center gap-1 text-xs">
                      <Target className="h-3 w-3" />
                      <span>{t("masterdata:LocationManuallyAdjusted")}</span>
                    </div>
                  )}

                  {userLocation.accuracy && userLocation.accuracy <= 15 && !userLocation.isManuallyAdjusted && (
                    <div className="text-primary flex items-center gap-1 text-xs">
                      <CheckCircle2 className="h-3 w-3" />
                      <span>{t("masterdata:HighGPSAccuracyLocationVeryPrecise")}</span>
                    </div>
                  )}

                  {showLocationAdjustment && (
                    <div className="bg-primary/10 border-primary/20 rounded-md border p-3">
                      <div className="text-foreground mb-2 text-sm font-medium">{t("masterdata:AdjustLocation")}</div>
                      <div className="text-muted-foreground mb-3 text-xs">
                        {t("masterdata:ClickOnMapToMarkExactLocation")}
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          size={ButtonSize.Sm}
                          variant={ButtonVariant.Outlined}
                          colorVariant={ButtonColorVariant.Secondary}
                          onClick={() => setShowLocationAdjustment(false)}
                          className="text-xs"
                        >
                          {t("common:Cancel")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : locationError ? (
                <Alert variant={ButtonColorVariant.Error} className="py-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">{locationError}</AlertDescription>
                </Alert>
              ) : null}

              {!userLocation && (
                <div className="flex justify-center sm:hidden">
                  <Button
                    onClick={getUserLocation}
                    disabled={isGettingLocation}
                    size={ButtonSize.Default}
                    className="w-full max-w-sm"
                  >
                    {isGettingLocation ? (
                      <>
                        <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2" />
                        {t("masterdata:GettingGPSPosition")}
                      </>
                    ) : (
                      <>
                        <MapPin className="mr-2 h-4 w-4" />
                        {t("masterdata:UseMyLocation")}
                      </>
                    )}
                  </Button>
                </div>
              )}

              {userLocation && (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                  <div className="space-y-4 lg:col-span-2">
                    <Button
                      onClick={getUserLocation}
                      disabled={isGettingLocation}
                      size={ButtonSize.Sm}
                      variant={ButtonVariant.Filled}
                      colorVariant={ButtonColorVariant.Secondary}
                      className="w-full"
                    >
                      {isGettingLocation ? (
                        <>
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} inheritColor className="mr-2" />
                          {t("masterdata:Updating")}
                        </>
                      ) : (
                        <>
                          <Navigation className="mr-2 h-4 w-4" />
                          {t("masterdata:UpdateLocation")}
                        </>
                      )}
                    </Button>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor={radiusSliderId} className="text-sm font-medium">
                          {t("common:SearchRadius")}
                        </Label>
                        <Badge
                          variant={BadgeVariant.Filled}
                          colorVariant={BadgeColorVariant.Secondary}
                          className="text-xs"
                        >
                          {radiusKm} {t("common:KM")}
                        </Badge>
                      </div>

                      <Slider
                        id={radiusSliderId}
                        min={5}
                        max={200}
                        step={5}
                        value={[radiusKm]}
                        onValueChange={(value) => setRadiusKm(value[0])}
                        className="w-full"
                      />

                      <div className="grid grid-cols-2 gap-2">
                        {[10, 25, 50, 100].map((radius) => (
                          <Button
                            key={radius}
                            variant={radiusKm === radius ? ButtonVariant.Filled : ButtonVariant.Outlined}
                            colorVariant={
                              radiusKm === radius ? ButtonColorVariant.Primary : ButtonColorVariant.Secondary
                            }
                            size={ButtonSize.Sm}
                            onClick={() => setRadiusKm(radius)}
                            className="text-xs"
                          >
                            {radius} {t("common:KM")}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="text-muted-foreground text-xs font-medium">{t("masterdata:LocationInfo")}</div>
                    <div className="flex items-center justify-between text-sm">
                      <span>{t("masterdata:FoundBranches")}:</span>
                      <Badge
                        variant={BadgeVariant.Outlined}
                        colorVariant={BadgeColorVariant.Secondary}
                        className="text-xs"
                      >
                        {branchesForMap.length}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span>{t("common:SearchRadius")}:</span>
                      <span className="text-muted-foreground text-xs">
                        {radiusKm} {t("common:KM")}
                      </span>
                    </div>
                    {userLocation.accuracy && (
                      <div className="flex items-center justify-between text-sm">
                        <span>{t("common:GPSAccuracy")}:</span>
                        {accuracyLevel && AccuracyLevelIcon && (
                          <div className="flex items-center gap-1">
                            <AccuracyLevelIcon className={`h-3 w-3 ${accuracyLevelColorClassName}`} />
                            <span className="text-muted-foreground text-xs">{accuracyLevel.level}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="lg:col-span-3">
                    <div className="bg-muted/20 h-70 overflow-hidden rounded-lg border lg:h-80">
                      <div className="bg-muted/50 flex items-center justify-between border-b p-2">
                        <div className="flex items-center gap-2">
                          <MapIcon className="text-primary h-3 w-3" />
                          <span className="text-xs font-medium">{t("masterdata:LiveMap")}</span>
                        </div>
                        <Badge
                          variant={BadgeVariant.Outlined}
                          colorVariant={BadgeColorVariant.Secondary}
                          className="text-xs"
                        >
                          {branchesForMap.length} {t("masterdata:Addresses")}
                        </Badge>
                      </div>
                      <div className="h-[calc(100%-41px)]">
                        <LocationMap
                          latitude={userLocation.latitude}
                          longitude={userLocation.longitude}
                          radiusKm={radiusKm}
                          accuracy={userLocation.accuracy}
                          locations={branchesForMap}
                          className="h-full w-full"
                          onLocationClick={handleLocationAdjustment}
                          allowLocationAdjustment={showLocationAdjustment}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="mt-2 flex min-h-87.5 flex-1 flex-col gap-1">
            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-lg leading-none font-semibold">
                  {userLocation ? t("masterdata:FoundBranches") : t("masterdata:AllBranches")}
                </div>
                <div className="text-muted-foreground mt-1 text-sm">
                  {userLocation
                    ? t("masterdata:BranchesInRadiusKm", { count: branchesForMap.length, radiusKm })
                    : t("masterdata:ActivateProximitySearchForPersonalizedView")}
                </div>
              </div>
              {userLocation && branchesForMap.length > 0 && (
                <Badge variant={BadgeVariant.Filled} colorVariant={BadgeColorVariant.Primary} className="text-xs">
                  {branchesForMap.length} {t("masterdata:Results")}
                </Badge>
              )}
            </div>
            <BranchTable
              key={branchTableName}
              name={branchTableName}
              defaultSorting={shouldUseBranchData ? PROXIMITY_SORTING : undefined}
              showDistance={shouldUseBranchData}
              showExportButton
              body={searchBody}
              pageSize={DEFAULT_PAGE_SIZE}
              showCreateButton={canManage}
              defaultRowActions={defaultRowActions}
              filters={filters}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onRowNavigate={createRowNavigatePath("/masterdata/branch")}
            />
          </div>
        </div>
      </CenteredLayout>
    </Can>
  );
}
